import classNames from "classnames";
import React, { MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";

import useTranslationModules from "../../../../../i18n/useTranslationModules";
import { setAddModuleModalOpen } from "../../../../../store/actions/admin/create-event";
import { resetScrollState } from "../../../../../store/actions/admin/event-panel";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { EPreviewTypes, FeatureFlagsEnum, NavMainItems, PageModule, PageModuleType, SessionTypesFeatures } from "../../../../../types/working-model";
import { getHomepageDefaultLanguage } from "../../../../../utils/get-language";
import { getTemplateClassName } from "../../../../../utils/utils";
import { ParamsProps } from "../../../../live-event/live-event";
import Footer from "../../../../live-event/marketing-page/footer/footer";
import MainEventBannerLive from "../../../../live-event/modules/main-event-banner-live/main-event-banner-live";
import { Dummy, ModuleMap } from "../editor/editor-main";
import LandingPreviewModal from "../editor/preview/eventPreview";
import { PostRegisterSideBar } from "./main-preview";

interface IPostRegisterEditorMainProps {
	pageModuleList: PageModule[];
	isHomePage?: boolean;
	isCustomPage?: boolean;
	customPageId?: number;
}

const PostRegisterEditorMain = React.forwardRef<HTMLDivElement, IPostRegisterEditorMainProps>(({ pageModuleList, isCustomPage, isHomePage, customPageId }, ref) => {
	const panelOpen = useTypedSelector(state => state.EventPanelReducer.panelOpen);
	const scrollToModule = useTypedSelector(state => state.EventPanelReducer.scrollToModule);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const editorSize = useTypedSelector(state => state.CreateEventReducer.editorSize);
	const previewMode = useTypedSelector(state => state.CreateEventReducer.previewMode);
	const loadingEvent = useTypedSelector(state => state.CreateEventReducer.loadingEvent);
	const blurEditor = useTypedSelector(state => state.CreateEventReducer.blurEditor);
	const displayLiveBanner = useTypedSelector(state => state.CreateEventReducer.displayLiveBanner);
	const featureFlags = useTypedSelector(event => event.FeatureFlagsReducer.featureFlags);

	const dispatch = useDispatch();

	const refMap = useRef<any>({});
	const containerRef = ref;

	const { language, customPath }: ParamsProps = useParams();

	const baseLanguage = getHomepageDefaultLanguage();

	useTranslationModules({ language: language ?? baseLanguage });

	function handleAddModule() {
		dispatch(setAddModuleModalOpen(true));
	}

	const handleScroll = useCallback(async (moduleId?: number) => {
		const moduleRef = moduleId && refMap.current[moduleId];

		if (containerRef && moduleRef) {
			(containerRef as MutableRefObject<HTMLDivElement>).current.scrollTop = moduleRef.offsetTop;
		}
	}, [containerRef]);

	useEffect(() => {
		if (scrollToModule !== null) {
			handleScroll(scrollToModule);
		}
		// after triggering the scroll, reset state incase 
		// user clicks same module after scrolling
		setTimeout(() => dispatch(resetScrollState()), 2000);
	}, [dispatch, scrollToModule, handleScroll]);

	const modules = useMemo(() => {
		if (!workingEvent || !pageModuleList) return [];
		return pageModuleList?.map((module: PageModule) => {
			const Module = ModuleMap[module.type];

			// Quizzes feature flag enables survey on homepage
			if (module.type === PageModuleType.survey && !featureFlags[FeatureFlagsEnum.quizzes]) {
				return Dummy;
			}

			// handleScroll prop because sponsors and Main Event Banner
			// have call to action buttons that can scroll to Homepage sections
			if (module.type === PageModuleType.agenda) {
				return <Module
					key={module.id}
					page_module={module}
					template={getTemplateClassName(workingEvent.template.name)}
					ref={(ref: any) => refMap.current[module.id || 0] = ref}
					handleScroll={handleScroll}
					language={language}
					baseLanguage={baseLanguage}
					isLandingPage
					isHomePage={isHomePage}
					isCustomPage={isCustomPage}
					customPageId={customPageId}
				/>;
			}
			if (module.type === PageModuleType.main_event_banner && (module.content?.enable_live_banner ?? true) && displayLiveBanner) {
				const timestampedSessions = workingEvent?.sessions.filter(session => SessionTypesFeatures[session.session_type].has_start_time);
				return (
					<MainEventBannerLive
						module={module}
						template={getTemplateClassName(workingEvent.template.name)}
						key={module.id}
						liveSessionList={timestampedSessions ?? []}
					/>
				);
			}
			return <Module
				key={module.id}
				page_module={module}
				template={getTemplateClassName(workingEvent.template.name)}
				ref={(ref: any) => refMap.current[module.id || 0] = ref}
				handleScroll={handleScroll}
				language={language}
				baseLanguage={baseLanguage}
				isLandingPage
			/>;
		});
	}, [
		baseLanguage,
		displayLiveBanner,
		featureFlags,
		handleScroll,
		language,
		pageModuleList,
		workingEvent,
	]);

	const template = getTemplateClassName(workingEvent?.template.name);

	const activePage = customPath ?? NavMainItems.Home;

	const activeCustomPages = workingEvent?.custom_pages?.filter(page => page.is_on);

	if (!workingEvent) return null;
	return (
		<>
			{
				workingEvent?.homepage?.event_main_nav_items && (
					<PostRegisterSideBar
						{...{
							eventMainNavItems: workingEvent?.homepage?.event_main_nav_items,
							template,
							logo: workingEvent?.homepage?.header?.logo
						}}
						customPages={activeCustomPages}
						activeMenuItem={activePage}
					/>

				)
			}
			<div className={
				classNames("post-register-main-body homepage-editor",
					{
						'panel-open': !!panelOpen,
						loading: loadingEvent && blurEditor
					},
					editorSize)}
			>
				{modules.length
					? modules
					: (
						<div className="page-empty-state">
							<h3>Add page content</h3>
							<p>Create a beautiful page with customized sections</p>
							<button
								className="button primary"
								onClick={handleAddModule}
							>
								Add
							</button>
						</div>
					)
				}
				<LandingPreviewModal open={previewMode === EPreviewTypes.Landing} />
				{workingEvent?.homepage?.footer ? (
					<Footer footer={workingEvent.homepage.footer} template={template} />
				) : null}
			</div>
		</>
	);
});

PostRegisterEditorMain.displayName = "PostRegisterEditorMain";
export default PostRegisterEditorMain;


